import { useEffect, useState } from "react";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import i18n from "../i18n";

// Mini hook i18n "safe" (même pattern que le reste de l'app)
function useI18nSafe() {
  const [lng, setLng] = useState(
    (i18n.resolvedLanguage || i18n.language || "fr").toLowerCase()
  );
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string): string => String((i18n.t as any)(key, { lng }));
  return { t };
}

type ExportButtonsProps = {
  data: any[]; // tableau brut (objets)
  fileName?: string;
  excelColumns?: { [key: string]: string }; // { label: "Nom à afficher", key: "champ" }
  pdfColumns?: { header: string; key: string }[];
};

export const ExportButtons = ({
  data,
  fileName = "export_stats",
  excelColumns,
  pdfColumns,
}: ExportButtonsProps) => {
  const { t } = useI18nSafe();

  const exportToExcel = () => {
    const formattedData = excelColumns
      ? data.map((item) => {
          const newObj: Record<string, any> = {};
          for (const [label, key] of Object.entries(excelColumns)) {
            newObj[label] = item[key];
          }
          return newObj;
        })
      : data;

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Feuille1");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `${fileName}.xlsx`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    autoTable(doc, {
      head: [pdfColumns?.map((c) => c.header) ?? Object.keys(data[0])],
      body: data.map((row) =>
        pdfColumns
          ? pdfColumns.map((c) => row[c.key])
          : Object.values(row)
      ),
    });
    doc.save(`${fileName}.pdf`);
  };

  return (
    <div className="flex justify-center mb-6 mt-4 space-x-4">
      <button
        onClick={exportToExcel}
        className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition text-sm"
      >
        {t("common:export_excel")}
      </button>
      <button
        onClick={exportToPDF}
        className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition text-sm"
      >
        {t("common:export_pdf")}
      </button>
    </div>
  );
};
